import { Stack, Typography, useTheme } from "@mui/material";
import type { ReactNode } from "react";

interface CardContainerProps {
  title: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  maxHeight?: string | number;
  enableScroll?: boolean;
}

export const CardContainer = ({
  title,
  children,
  maxHeight = "calc(100vh - 140px)",
  enableScroll = false,
}: CardContainerProps) => {
  const { colors, customSpacing, radius, customFontWeight, width } = useTheme();

  return (
    <Stack
      sx={{
        width: width.sm,
        maxWidth: "100%",
        maxHeight: enableScroll ? maxHeight : "none",
        backgroundColor: colors.background.secondary,
        boxShadow: "none",
        borderRadius: radius["2xl"],
        border: `1px solid ${colors.border.primary}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        p: 0,
        position: "relative",
      }}
    >
      <Stack
        sx={{
          padding: customSpacing.xl,
          paddingBottom: customSpacing.md,
          flexShrink: 0,
        }}
      >
        {typeof title === "string" ? (
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.text.primary,
              fontWeight: customFontWeight.bold,
            }}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
      </Stack>

      <Stack
        sx={{
          flex: 1,
          overflow: enableScroll ? "auto" : "visible",
          padding: customSpacing.xl,
          paddingTop: enableScroll ? customSpacing.lg : customSpacing.xl,
          paddingBottom: enableScroll ? customSpacing.md : customSpacing.xl,
          minHeight: 0,
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: colors.border.secondary,
            borderRadius: "3px",
            "&:hover": {
              backgroundColor: colors.border.primary,
            },
          },
          scrollbarWidth: "thin",
          scrollbarColor: `${colors.background.overlay} transparent`,
        }}
        spacing={customSpacing.xl}
      >
        {children}
      </Stack>

      {enableScroll && (
        <Stack
          sx={{
            height: customSpacing.xl,
            backgroundColor: colors.background.secondary,
            flexShrink: 0,
            position: "relative",
          }}
        />
      )}
      {enableScroll && (
        <Stack
          sx={{
            height: customSpacing["5xl"],
            background: () =>
              `linear-gradient(180deg, transparent 0%, ${colors.background.secondary} 100%)`,
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            pointerEvents: "none",
          }}
        />
      )}
    </Stack>
  );
};
